import React, { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  LinearProgress,
  Avatar,
  Fade,
  Chip,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as BackIcon,
  ArrowForward as ArrowIcon,
  LockReset as LockResetIcon,
  School as StudentIcon,
  Campaign as ClubIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import api from '../../api';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function normalizeRole(rawRole) {
  const r = (rawRole || '').trim().toLowerCase();
  if (!r) return '';
  const aliases = {
    'club': 'club_member',
    'clubmember': 'club_member',
    'club-member': 'club_member',
    'club member': 'club_member',
    'collegeadmin': 'college_admin',
    'college-admin': 'college_admin',
    'college admin': 'college_admin',
  };
  return aliases[r] || r;
}

const roleMeta = {
  student: {
    label: 'Student',
    color: '#1E3A8A',
    icon: StudentIcon,
    backTo: '/login/student',
  },
  club_member: {
    label: 'Club Member',
    color: '#016f57',
    icon: ClubIcon,
    backTo: '/login/club-member',
  },
  admin: {
    label: 'Admin',
    color: '#6b7280',
    icon: AdminIcon,
    backTo: '/login/admin',
  },
};

export default function ForgotPassword({ defaultRole }) {
  const navigate = useNavigate();
  const query = useQuery();

  const roleFromQuery = (query.get('role') || '').trim();
  const role = normalizeRole(roleFromQuery || defaultRole || '');
  const meta = roleMeta[role] || null;

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const cleaned = email.trim();
    if (!cleaned) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      await api.post('users/password-reset/', { email: cleaned, role: meta ? role : undefined });
      setSuccess(true);
    } catch (err) {
      // Backend intentionally returns generic success in most cases.
      // Keep frontend messaging generic too.
      const msg =
        err?.error ||
        err?.detail ||
        'Unable to process your request right now. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const accent = meta?.color || '#1E3A8A';
  const RoleIcon = meta?.icon || LockResetIcon;

  const backHref = meta?.backTo || '/login';

  const successMessage = (() => {
    if (!meta) return 'If an account exists for this email, a password reset link has been sent.';
    return `If an account exists for this email (${meta.label}), a password reset link has been sent.`;
  })();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#fafbfc' }}>
      <Navbar />

      <Box sx={{ flex: 1, py: { xs: 4, md: 7 } }}>
        <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
          <Fade in timeout={600}>
            <Card
              elevation={0}
              sx={{
                borderRadius: { xs: 3, sm: 5 },
                border: '1px solid rgba(226, 232, 240, 0.9)',
                background: 'rgba(255, 255, 255, 0.9)',
                WebkitBackdropFilter: 'blur(12px)',
                backdropFilter: 'blur(12px)',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(15, 23, 42, 0.08)',
              }}
            >
              {loading && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { background: accent } }} />}

              <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                <Stack spacing={2.5}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Avatar
                      sx={{
                        width: { xs: 42, sm: 46 },
                        height: { xs: 42, sm: 46 },
                        background: `linear-gradient(135deg, ${accent} 0%, rgba(2, 132, 199, 0.9) 100%)`,
                      }}
                    >
                      <RoleIcon sx={{ fontSize: { xs: 20, sm: 22 }, color: 'white' }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.1, fontSize: { xs: '1.9rem', sm: '2.1rem' } }}>
                        Forgot password
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, lineHeight: 1.45 }}>
                        We’ll email you a reset link (valid for 30 minutes).
                      </Typography>
                    </Box>
                    {meta && (
                      <Chip
                        label={meta.label}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'rgba(15, 23, 42, 0.04)',
                          color: '#0f172a',
                          alignSelf: { xs: 'flex-start', sm: 'center' },
                        }}
                      />
                    )}
                  </Stack>

                  {success ? (
                    <Alert severity="success" sx={{ borderRadius: 3 }}>
                      {successMessage}
                    </Alert>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#334155' }}>
                      Enter the email associated with your account. For security, we won’t confirm whether the email exists.
                    </Typography>
                  )}

                  {error && (
                    <Alert severity="error" sx={{ borderRadius: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Stack spacing={2}>
                      <TextField
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        label="Email"
                        type="email"
                        autoComplete="email"
                        fullWidth
                        disabled={loading || success}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ color: '#94a3b8' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 4,
                            backgroundColor: 'rgba(248, 250, 252, 0.7)',
                          },
                        }}
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={loading || success}
                        endIcon={<ArrowIcon />}
                        sx={{
                          py: 1.6,
                          borderRadius: 4,
                          textTransform: 'none',
                          fontWeight: 800,
                          background: `linear-gradient(135deg, ${accent} 0%, ${accent} 100%)`,
                          boxShadow: `0 12px 30px ${accent}33`,
                          '&:hover': {
                            boxShadow: `0 16px 40px ${accent}40`,
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        Send reset link
                      </Button>

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        justifyContent="center"
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                      >
                        <Button
                          variant="text"
                          startIcon={<BackIcon />}
                          onClick={() => navigate(backHref)}
                          sx={{ textTransform: 'none', fontWeight: 800, color: '#475569', width: { xs: '100%', sm: 'auto' } }}
                        >
                          Back to login
                        </Button>
                        <Button
                          component={RouterLink}
                          to="/login"
                          variant="text"
                          sx={{
                            textTransform: 'none',
                            fontWeight: 800,
                            color: accent,
                            width: { xs: '100%', sm: 'auto' },
                            '&:hover': { backgroundColor: 'rgba(2, 132, 199, 0.06)' },
                          }}
                        >
                          Change role
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
